/**
 * A promise wrapper over the Stockfish Web Worker.
 *
 * The worker script is the stockfish dist file itself — it speaks UCI over
 * postMessage, so there is no wrapper worker to maintain. The build served
 * from /stockfish/ is `lite-single`: single-threaded (no SharedArrayBuffer,
 * so no COOP/COEP headers needed anywhere) with the small NNUE net.
 *
 * Nothing is fetched until `createEngine` is called, so visitors who never
 * open an engine game never download the 7 MB of weights.
 */

export interface EngineSettings {
  /** Honest strength cap via UCI_LimitStrength. Null plays at full strength. */
  readonly elo: number | null
  readonly movetimeMs: number
}

export interface Engine {
  /** Best move for a FEN, as UCI (`e2e4`, `e7e8q`). One search at a time. */
  readonly bestMove: (fen: string) => Promise<string>
  readonly dispose: () => void
}

const ENGINE_URL = '/stockfish/stockfish-18-lite-single.js'
/** Covers the wasm fetch + compile on a slow phone; init is once per game. */
const INIT_TIMEOUT_MS = 30_000

const textOf = (event: MessageEvent): string =>
  typeof event.data === 'string' ? event.data : ''

/** Wait for one line matching `test`, then stop listening. */
const lineFrom = (
  worker: Worker,
  test: (line: string) => boolean,
  timeoutMs: number,
  what: string,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      worker.removeEventListener('message', onMessage)
      reject(new Error(`engine: timed out waiting for ${what}`))
    }, timeoutMs)
    const onMessage = (event: MessageEvent): void => {
      const line = textOf(event)
      if (!test(line)) return
      window.clearTimeout(timer)
      worker.removeEventListener('message', onMessage)
      resolve(line)
    }
    worker.addEventListener('message', onMessage)
  })

export const createEngine = async (settings: EngineSettings): Promise<Engine> => {
  const worker = new Worker(ENGINE_URL)
  const send = (command: string): void => worker.postMessage(command)

  try {
    const ready = lineFrom(worker, (line) => line === 'uciok', INIT_TIMEOUT_MS, 'uciok')
    send('uci')
    await ready

    if (settings.elo !== null) {
      send('setoption name UCI_LimitStrength value true')
      send(`setoption name UCI_Elo value ${settings.elo}`)
    }

    const isReady = lineFrom(worker, (line) => line === 'readyok', INIT_TIMEOUT_MS, 'readyok')
    send('isready')
    await isReady
  } catch (cause) {
    worker.terminate()
    throw cause
  }

  let disposed = false
  let searching = false

  return {
    bestMove: async (fen: string): Promise<string> => {
      if (disposed) throw new Error('engine: disposed')
      if (searching) throw new Error('engine: a search is already running')
      searching = true
      try {
        const answer = lineFrom(
          worker,
          (line) => line.startsWith('bestmove '),
          // The engine honours movetime closely; the margin is for wasm hiccups.
          settings.movetimeMs + 10_000,
          'bestmove',
        )
        send(`position fen ${fen}`)
        send(`go movetime ${settings.movetimeMs}`)
        const line = await answer
        const uci = line.split(/\s+/)[1]
        if (uci === undefined || uci === '(none)') throw new Error(`engine: no move in ${JSON.stringify(line)}`)
        return uci
      } finally {
        searching = false
      }
    },
    dispose: (): void => {
      disposed = true
      worker.terminate()
    },
  }
}
